import { Injectable } from '@angular/core';
import { Course } from 'lms-api';
import { LMSDataService } from 'lms-api';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { UserCourseDataService } from 'lms-api';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CourseFilterService {
  public showFilterUI: boolean = true;
  public courseListDisplayType: string;
  public filterIterator: number;
  public filterGroupsUpdated: BehaviorSubject<any>;

  public filterCriteria: { Statuses: Array<string>, Tags: Array<string>, Types: Array<string>, Credits: Array<string>, Miscellany: Array<string>, Text: string };
  public filterGroups: { Statuses: any, Tags: any, Types: any, Libraries: any, Credits: any };

  constructor(private lmsDataService: LMSDataService, private ucdService: UserCourseDataService) {
    this.resetFilterCriteria();
    this.courseListDisplayType = 'list';
    this.filterIterator = 0;
    this.filterGroups = { Statuses: {}, Tags: {}, Types: {}, Libraries: {}, Credits: {} };
    this.filterGroupsUpdated = new BehaviorSubject(null);

    combineLatest(ucdService.User, ucdService.Courses$).pipe(
      tap(([user, courses]) => {
        if (courses == null) return;

        this.filterGroups = { Statuses: {}, Tags: {}, Types: {}, Libraries: {}, Credits: {} };

        courses.forEach(course => {
          if (course.archived_course || !course.canAccess) return;

          if (course.userData.Status) {
            if (this.filterGroups.Statuses[course.userData.Status] == null) {
              this.filterGroups.Statuses[course.userData.Status] = 0;
            }
            this.filterGroups.Statuses[course.userData.Status]++;
          }

          if (course.tags != null) {
            course.tags.forEach(tag => {
              if (this.filterGroups.Tags[tag] == null) {
                this.filterGroups.Tags[tag] = 0;
              }
              this.filterGroups.Tags[tag]++;
            });
          };

          if (this.filterGroups.Types[course.typeid] == null) {
            this.filterGroups.Types[course.typeid] = { count: 0, type: course.type, typeid: course.typeid };
          }
          this.filterGroups.Types[course.typeid].count++;

          if (this.filterGroups.Libraries[course.library] == null) {
            this.filterGroups.Libraries[course.library] = { count: 0, types: [] };
          }
          this.filterGroups.Libraries[course.library].count++;
          if (!this.filterGroups.Libraries[course.library].types.includes(course.typeid)) {
            this.filterGroups.Libraries[course.library].types.push(course.typeid);
          }

          if (this.filterGroups.Credits[course.credits] == null) {
            this.filterGroups.Credits[course.credits] = 0;
          }
          this.filterGroups.Credits[course.credits]++;
        });
        this.filterGroupsUpdated.next(this.filterGroups);
      })
    ).subscribe();
  }

  public filterCourseList(courses: Course[]) {
    if (courses == null) return [];
    return courses.filter(course => this.courseMatchesFilter(course));
  }

  private courseMatchesFilter(course: Course) {
    let matches = { Status: false, Tags: this.filterCriteria.Tags.length == 0, Text: false, Type: false, Credits: false, Miscellany: true };
    matches.Status = this.filterCriteria.Statuses.length == 0 || this.filterCriteria.Statuses.includes(course.userData.Status);
    matches.Type = this.filterCriteria.Types.length == 0 || this.filterCriteria.Types.includes(course.typeid);
    matches.Credits = this.filterCriteria.Credits.length == 0 || this.filterCriteria.Credits.includes(course.credits);
    let miscCounts = { needed: 0, met: 0 };
    this.filterCriteria.Miscellany.forEach(miscKey => {
      if (miscKey == 'faves') {
        miscCounts.needed++;
        miscCounts.met += course.userData.Favorite ? 1 : 0;
      }
      if (miscKey == 'paid') {
        miscCounts.needed++;
        miscCounts.met += course.userData.Paid ? 1 : 0;
      }
      if (miscKey.startsWith('approvedstates_')) {
        miscCounts.needed++;
        let state = miscKey.split('_')[1];
        miscCounts.met += course.approved_states.split(',').includes(state) ? 1 : 0;
      }
    });
    matches.Miscellany = miscCounts.met == miscCounts.needed;

    matches.Tags = this.filterCriteria.Tags.length == 0 || this.filterCriteria.Tags.filter(t => course.tags.includes(t)).length > 0;

    if (this.filterCriteria.Text == null) { matches.Text = true; }
    else {
      try {
        let textRegex = new RegExp(this.filterCriteria.Text, 'i');
        if (course.description.match(textRegex)) {
          matches.Text = true;
        }
        if (course.title.match(textRegex)) {
          matches.Text = true;
        }
        if (course.netforum_course_id == this.filterCriteria.Text) {
          matches.Text = true;
        }
        course.authors.forEach(author => {
          if (author.name.match(textRegex)) {
            matches.Text = true;
          }
        });
      } catch (ex) { }
    }

    return matches.Status && matches.Tags && matches.Text && matches.Type && matches.Credits && matches.Miscellany;
  }

  public resetFilterCriteria() {
    this.filterCriteria = { Statuses: [], Tags: [], Types: [], Credits: [], Miscellany: [], Text: null };
    this.filterIterator++;
  }
}
